import camelCase from 'lodash/camelCase';
import { useTranslation } from 'react-i18next';

import { PLATFORM_THEME_COLORS, Typography } from '@thinknet-gmbh/platform-common-ui';

import { IEmployee, IJob, ILocation, ISupervisor } from 'src/pages/jobs/jobs.interfaces';
import { displayAddress } from 'src/common/helpers/common.helpers';
import { JobDetailsViewModal } from 'src/pages/jobs/components/job-details-page/components/job-details-view/job-details-view.component';

import EmployeeRowPopover from './employee-row-popover.component';
import {
  InfoBlock,
  InfoWrapper,
  InfoElement,
  EmployeeListWrapper,
  EmployeesWrapper,
} from './job-profile-info.styles';
import { JOB_DETAIL_VIEW_MODALS } from './job-profile-info.constants';

const JobProfileInfo = ({
  job,
  handleModals,
}: {
  job: IJob;
  handleModals: (modalData: JobDetailsViewModal) => void;
}) => {
  const { t } = useTranslation(['common', 'jobs']);

  return (
    <InfoWrapper>
      <InfoBlock>
        <InfoElement>
          <Typography weight="bold">{`${t('common:labels.jobTitle')}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>{job.title}</Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{`${t('jobs:labels.activityKey')}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>{job?.activityKey || '-'}</Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{`${t('common:labels.description')}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {job.description || '-'}
          </Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{`${t('common:labels.company')}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>{job.company.name}</Typography>
        </InfoElement>
      </InfoBlock>
      <div>
        {job.jobLocations.map((location: ILocation) => (
          <InfoBlock key={location._id}>
            <InfoElement>
              <Typography weight="bold">{`${t('common:labels.location')}:`}</Typography>
              <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                {location.companyLocation.name}
              </Typography>
            </InfoElement>
            <InfoElement>
              <Typography weight="bold">{`${t('common:labels.businessAddress')}:`}</Typography>
              <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                {displayAddress(location.companyBusinessAddress)}
              </Typography>
            </InfoElement>
            <InfoElement>
              <Typography weight="bold">{`${t('common:labels.department')}:`}</Typography>
              <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                {location.companyDepartment.name}
              </Typography>
            </InfoElement>
            <div>
              {location.jobSupervisors.map((supervisor: ISupervisor) => (
                <div key={supervisor._id}>
                  <InfoElement>
                    <Typography weight="bold">{`${t(
                      'jobs:labels.supervisorJobTitle'
                    )}:`}</Typography>
                    <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                      {supervisor?.supervisorJobTitle || '-'}
                    </Typography>
                  </InfoElement>
                  <InfoElement>
                    <Typography weight="bold">{`${t('jobs:labels.supervisorName')}:`}</Typography>
                    <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                      {supervisor.supervisorMember
                        ? supervisor.supervisorMember.profile.displayName
                        : '-'}
                    </Typography>
                  </InfoElement>
                  <InfoElement>
                    <Typography weight="bold">{`${t('jobs:labels.jobPlaces')}:`}</Typography>
                    <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                      {supervisor.jobPlacesCount}
                    </Typography>
                  </InfoElement>
                  <EmployeesWrapper>
                    <EmployeeListWrapper>
                      {supervisor.jobPlacesCount > supervisor.assignedMembers.length && (
                        <EmployeeRowPopover
                          setJobToEmployeeHandler={() =>
                            handleModals({
                              modal: JOB_DETAIL_VIEW_MODALS.setJobToEmployee,
                              data: {
                                job,
                                jobLocationId: location._id,
                                jobSupervisorId: supervisor._id,
                                employee: null,
                              },
                            })
                          }
                        />
                      )}
                      {supervisor.assignedMembers.map((employee: IEmployee) => (
                        <EmployeeRowPopover
                          key={employee._id}
                          employee={employee}
                          removeEmployeeFromJobPlace={() =>
                            handleModals({
                              modal: JOB_DETAIL_VIEW_MODALS.removeFromJob,
                              data: {
                                job,
                                jobLocationId: location._id,
                                jobSupervisorId: supervisor._id,
                                employee,
                              },
                            })
                          }
                        />
                      ))}
                    </EmployeeListWrapper>
                  </EmployeesWrapper>
                </div>
              ))}
            </div>
          </InfoBlock>
        ))}
      </div>
      <InfoBlock>
        <InfoElement>
          <Typography weight="bold">{`${t('employee:labels.workingHours')}:`}</Typography>
          <Typography
            fontColor={PLATFORM_THEME_COLORS.primary.grey}
          >{`${job.workingHours} hr`}</Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{`${t('employee:labels.salaryType')}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            {t(`employee:workingConditions.labels.${camelCase(job.salaryType)}`)}
          </Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{`${t('jobs:labels.vacation')}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>{job.vacation}</Typography>
        </InfoElement>
        <InfoElement>
          <Typography weight="bold">{`${t('jobs:labels.appraisalReview')}:`}</Typography>
          <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
            ${job.appraisalReview}
          </Typography>
        </InfoElement>
      </InfoBlock>
    </InfoWrapper>
  );
};

export default JobProfileInfo;
